import { CardShell } from '@atlas/ui-components'
import { Button, Chip, Input } from '@atlas/ui-primitives'
import { useState } from 'react'
import { extractApiErrorMessage } from '@/adapters/workflow-admin-http-error'
import { PageHeader } from '@/components/editorial/PageHeader'
import { PROMPT_TYPE_LABEL, type PromptType } from '@/domain/prompt.types'
import { usePromptDefaults, useUpdatePromptDefaults } from '@/hooks/usePrompts'
import { notification } from '@/notifications/notification-store'

interface DefaultRow {
	promptType: string
	modelConfig: Record<string, unknown>
	isTenantOverride: boolean
}

/**
 * Tenant model-config defaults, per prompt type.
 *
 * Editing one affects only SUBSEQUENT publishes: a published version carries its own
 * frozen copy, so changing a default here cannot retroactively alter what a past run
 * was configured to do. Stated on the page because that is the non-obvious part.
 */
export function PromptDefaultsPage() {
	const defaults = usePromptDefaults()
	const update = useUpdatePromptDefaults()
	const [editing, setEditing] = useState<string | null>(null)

	return (
		<div>
			<PageHeader
				eyebrow="Administration"
				title="Prompt defaults"
				subtitle="Model configuration per prompt type. Authors inherit these and rarely override them."
			/>

			<p className="mb-4 rounded-ds-md border border-ds-border bg-ds-surface p-3 text-ds-sm text-ds-text-muted">
				A change here applies to future publishes only. Versions already published keep the configuration they were
				frozen with, so past runs stay explainable.
			</p>

			{/* Supplements the status bar, naming what the server refused beside the rows. */}
			{update.isError ? (
				<p role="alert" className="mb-3 text-ds-sm text-ds-danger">
					{extractApiErrorMessage(update.error, 'The default could not be saved.')}
				</p>
			) : null}

			{defaults.isLoading ? (
				<p className="text-ds-text-muted">Loading…</p>
			) : (
				<ul className="space-y-3">
					{(defaults.data ?? []).map((row) => (
						<DefaultsRow
							key={row.promptType}
							row={row as DefaultRow}
							isEditing={editing === row.promptType}
							isSaving={update.isPending}
							onEdit={() => setEditing(row.promptType)}
							onCancel={() => setEditing(null)}
							onSave={(modelConfig) =>
								update.mutate(
									{ promptType: row.promptType, modelConfig },
									{
										// A tenant-wide default changing with no confirmation at all read as
										// "did that save?" — the row simply closed.
										onSuccess: () => {
											setEditing(null)
											notification.success(`Defaults updated for ${row.promptType}.`)
										},
										onError: (error) =>
											notification.error(extractApiErrorMessage(error, 'The default could not be saved.')),
									},
								)
							}
						/>
					))}
				</ul>
			)}
		</div>
	)
}

function DefaultsRow({
	row,
	isEditing,
	isSaving,
	onEdit,
	onCancel,
	onSave,
}: {
	row: DefaultRow
	isEditing: boolean
	isSaving: boolean
	onEdit: () => void
	onCancel: () => void
	onSave: (modelConfig: Record<string, unknown>) => void
}) {
	const config = row.modelConfig
	const [model, setModel] = useState(String(config.model ?? ''))
	const [temperature, setTemperature] = useState(String(config.temperature ?? ''))

	const label = PROMPT_TYPE_LABEL[row.promptType as PromptType] ?? row.promptType

	return (
		<CardShell as="li" className="p-4">
			<div className="flex items-start justify-between gap-3">
				<div>
					<p className="text-ds-sm font-medium text-ds-text">{label}</p>
					{/* A shipped fallback must not look like a choice the tenant made. */}
					<Chip variant="muted" size="sm">
						{row.isTenantOverride ? 'tenant override' : 'shipped default'}
					</Chip>
				</div>

				{isEditing ? (
					<div className="flex gap-2">
						<Button size="sm" variant="secondary" onPress={onCancel} isDisabled={isSaving}>
							Cancel
						</Button>
						<Button
							size="sm"
							isDisabled={isSaving}
							onPress={() =>
								onSave({
									...(model === '' ? {} : { model }),
									...(temperature === '' ? {} : { temperature: Number(temperature) }),
								})
							}
						>
							{isSaving ? 'Saving…' : 'Save'}
						</Button>
					</div>
				) : (
					<Button size="sm" variant="secondary" onPress={onEdit}>
						Edit
					</Button>
				)}
			</div>

			{isEditing ? (
				<div className="mt-3 grid gap-2 sm:grid-cols-2">
					<label className="block">
						<span className="mb-1 block text-ds-2xs text-ds-text-subtle">Model</span>
						<Input
							value={model}
							aria-label={`${label} model`}
							className="font-mono"
							onChange={(e) => setModel(e.target.value)}
						/>
					</label>
					<label className="block">
						<span className="mb-1 block text-ds-2xs text-ds-text-subtle">Temperature</span>
						<Input
							type="number"
							step="0.1"
							value={temperature}
							aria-label={`${label} temperature`}
							onChange={(e) => setTemperature(e.target.value)}
						/>
					</label>
				</div>
			) : (
				<dl className="mt-3 grid gap-2 text-ds-sm sm:grid-cols-3">
					<div>
						<dt className="text-ds-2xs text-ds-text-subtle">Model</dt>
						<dd className="font-mono text-ds-text">{String(config.model ?? '—')}</dd>
					</div>
					<div>
						<dt className="text-ds-2xs text-ds-text-subtle">Temperature</dt>
						<dd className="font-mono text-ds-text">{String(config.temperature ?? '—')}</dd>
					</div>
					<div>
						<dt className="text-ds-2xs text-ds-text-subtle">Max tokens</dt>
						<dd className="font-mono text-ds-text">{String(config.maxTokens ?? '—')}</dd>
					</div>
				</dl>
			)}
		</CardShell>
	)
}
